import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminProtectedRoute } from "./components/AdminProtectedRoute";
import { Toaster } from "./components/ui/sonner";
import { initializeFxRate } from "./utils/multiCurrency";

// Public pages
import Home from "./pages/Home";
import Senders from "./pages/Senders";
import Colmados from "./pages/Colmados";
import About from "./pages/About";
import Trust from "./pages/Trust";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Compliance from "./pages/Compliance";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import Investors from "./pages/Investors";
import Login from "./pages/Login";
import AuthVerify from "./pages/AuthVerify";
import VerifyPage from "./pages/VerifyPage";
import NotFound from "./pages/NotFound";
import Receipt from "./pages/Receipt";
import PayPage from "./pages/PayPage";
import RedeemPage from "./pages/RedeemPage";
import BeneficiaryRedemption from "./pages/BeneficiaryRedemption";
import BeneficiaryPortal from "./pages/BeneficiaryPortal";
import ApprovePurchase from "./pages/ApprovePurchase";
import MerchantRegistration from "./pages/MerchantRegistration";

// Admin portal pages (separate from main app)
import AdminLogin from "./pages/admin/AdminLogin";
import AdminVerify from "./pages/admin/AdminVerify";
import AdminResetPassword from "./pages/admin/AdminResetPassword";
import AdminDashboardPortal from "./pages/admin/AdminDashboardPortal";
import AdminUsersView from "./pages/admin/AdminUsersView";
import AdminTransactionsView from "./pages/admin/AdminTransactionsView";
import AdminAuthorizationsView from "./pages/admin/AdminAuthorizationsView";
import AdminAnalyticsView from "./pages/admin/AdminAnalyticsView";
import AdminFinancialAnalytics from "./pages/admin/AdminFinancialAnalytics";
import AdminAuditView from "./pages/admin/AdminAuditView";
import AdminTreasuryView from "./pages/admin/AdminTreasuryView";
import AdminPricingView from "./pages/admin/AdminPricingView";
import AdminPricingSimulatorView from "./pages/admin/AdminPricingSimulatorView";
import InvestorDemoMode from "./pages/admin/InvestorDemoMode";
import AdminFXView from "./pages/admin/AdminFXView";
import AdminEnforcementView from "./pages/admin/AdminEnforcementView";
import AdminSettlementsView from "./pages/admin/AdminSettlementsView";
import AdminLimitsView from "./pages/admin/AdminLimitsView";
import AdminRiskView from "./pages/admin/AdminRiskView";
import AdminSupportView from "./pages/admin/AdminSupportView";
import AdminOnboardingView from "./pages/admin/AdminOnboardingView";
import AdminHealthView from "./pages/admin/AdminHealthView";
import AdminNotificationsView from "./pages/admin/AdminNotificationsView";
import AdminApplicationReview from "./pages/admin/AdminApplicationReview";
import AdminDemoAnalytics from "./pages/admin/AdminDemoAnalytics";

// Dashboard pages
import SenderDashboard from "./pages/dashboards/SenderDashboard";
import MerchantDashboard from "./pages/dashboards/MerchantDashboard";
import AdminDashboard from "./pages/dashboards/AdminDashboard";
import AdminAnalytics from "./pages/dashboards/AdminAnalytics";

function App() {
  // Initialize FX rate on app load for immediate availability
  useEffect(() => {
    initializeFxRate();
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <LanguageProvider>
          <div className="App">
            <BrowserRouter>
              <Routes>
                {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/senders" element={<Senders />} />
              <Route path="/colmados" element={<Colmados />} />
              <Route path="/about" element={<About />} />
              <Route path="/trust" element={<Trust />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/compliance" element={<Compliance />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/investors" element={<Investors />} />
              <Route path="/login" element={<Login />} />
              
              {/* Magic Link Verification Routes */}
              <Route path="/verify" element={<VerifyPage />} />
              <Route path="/auth/verify" element={<AuthVerify />} />
              
              {/* QR Code Payment Route */}
              <Route path="/pay/:code" element={<PayPage />} />
              
              {/* QR Code Redemption Route (for merchants) */}
              <Route path="/redeem/:code" element={<RedeemPage />} />
              
              {/* Beneficiary Secure Access Route (for beneficiaries) */}
              <Route path="/b/:accessToken" element={<BeneficiaryRedemption />} />
              
              {/* Beneficiary Portal - Public lookup for authorizations */}
              <Route path="/beneficiary" element={<BeneficiaryPortal />} />
              
              {/* Purchase Approval Route (for beneficiaries approving colmado purchases) */}
              <Route path="/approve-purchase" element={<ApprovePurchase />} />
              
              {/* Merchant Self-Registration (public) */}
              <Route path="/registro-colmado" element={<MerchantRegistration />} />
              <Route path="/merchant-registration" element={<MerchantRegistration />} />
              
              <Route path="/receipt/:receiptId" element={<Receipt />} />

              {/* Admin Portal Routes (completely separate from /app/*) */}
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/verify" element={<AdminVerify />} />
              <Route path="/admin/reset-password" element={<AdminResetPassword />} />
              <Route 
                path="/admin/dashboard" 
                element={
                  <AdminProtectedRoute>
                    <AdminDashboardPortal />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/users" 
                element={
                  <AdminProtectedRoute>
                    <AdminUsersView />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/transactions" 
                element={
                  <AdminProtectedRoute>
                    <AdminTransactionsView />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/authorizations" 
                element={
                  <AdminProtectedRoute>
                    <AdminAuthorizationsView />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/analytics" 
                element={
                  <AdminProtectedRoute>
                    <AdminAnalyticsView />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/analytics/financial" 
                element={
                  <AdminProtectedRoute>
                    <AdminFinancialAnalytics />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/audit" 
                element={
                  <AdminProtectedRoute>
                    <AdminAuditView />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/treasury" 
                element={
                  <AdminProtectedRoute>
                    <AdminTreasuryView />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/pricing" 
                element={
                  <AdminProtectedRoute>
                    <AdminPricingView />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/pricing-simulator" 
                element={
                  <AdminProtectedRoute>
                    <AdminPricingSimulatorView />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/investor-demo" 
                element={
                  <AdminProtectedRoute>
                    <InvestorDemoMode />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/fx" 
                element={
                  <AdminProtectedRoute>
                    <AdminFXView />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/enforcement" 
                element={
                  <AdminProtectedRoute>
                    <AdminEnforcementView />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/settlements" 
                element={
                  <AdminProtectedRoute>
                    <AdminSettlementsView />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/limits" 
                element={
                  <AdminProtectedRoute>
                    <AdminLimitsView />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/risk" 
                element={
                  <AdminProtectedRoute>
                    <AdminRiskView />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/support" 
                element={
                  <AdminProtectedRoute>
                    <AdminSupportView />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/onboarding" 
                element={
                  <AdminProtectedRoute>
                    <AdminOnboardingView />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/health" 
                element={
                  <AdminProtectedRoute>
                    <AdminHealthView />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/notifications" 
                element={
                  <AdminProtectedRoute>
                    <AdminNotificationsView />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/merchant-applications" 
                element={
                  <AdminProtectedRoute>
                    <AdminApplicationReview />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/demo-analytics" 
                element={
                  <AdminProtectedRoute>
                    <AdminDemoAnalytics />
                  </AdminProtectedRoute>
                } 
              />

              {/* Protected Dashboard Routes (for non-admin users) */}
              <Route
                path="/app/sender"
                element={
                  <ProtectedRoute allowedRoles={['sender']}>
                    <SenderDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/merchant"
                element={
                  <ProtectedRoute allowedRoles={['merchant']}>
                    <MerchantDashboard />
                  </ProtectedRoute>
                }
              />
              {/* Legacy admin routes - redirect to new admin portal */}
              <Route
                path="/app/admin"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/admin/analytics"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminAnalytics />
                  </ProtectedRoute>
                }
              />

              {/* Catch-all route - MUST be last */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </div>
      </LanguageProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
